import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useProfiles } from '../hooks/useDatabase'
import { Button, Input, Dialog, DialogBody, DialogFooter, Form, FormGroup, FormLabel, FormError } from './ui'
import './ProfileManager.css'

export const ProfileManager: React.FC = () => {
  const { user, signOut } = useAuth()
  const { 
    profiles, 
    loading, 
    error, 
    createProfile, 
    updateProfile, 
    deleteProfile,
    isCreating,
    isUpdating,
    isDeleting
  } = useProfiles()
  const [isEditing, setIsEditing] = useState(false)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [editingProfile, setEditingProfile] = useState<any>(null)
  const [formData, setFormData] = useState({
    full_name: '',
    avatar_url: '',
  })
  const [formError, setFormError] = useState('')

  // Find current user's profile
  const currentUserProfile = profiles.find(profile => profile.id === user?.id)

  useEffect(() => {
    if (editingProfile) {
      setFormData({
        full_name: editingProfile.full_name || '',
        avatar_url: editingProfile.avatar_url || '',
      })
    }
  }, [editingProfile])

  const handleCreateProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return

    setFormError('')
    try {
      await createProfile({
      id: user.id,
      email: user.email!,
      full_name: formData.full_name,
      avatar_url: formData.avatar_url,
    })
      setFormData({ full_name: '', avatar_url: '' })
      setIsCreateDialogOpen(false)
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Failed to create profile')
    }
  }

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingProfile) return

    setFormError('')
    try {
      await updateProfile(editingProfile.id, formData)
      setIsEditing(false)
      setEditingProfile(null)
      setFormData({ full_name: '', avatar_url: '' })
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Failed to update profile')
    }
  }

  const handleDeleteProfile = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this profile?')) {
      try {
      await deleteProfile(id)
      } catch (error) {
        alert(error instanceof Error ? error.message : 'Failed to delete profile')
      }
    }
  }

  const startEditing = (profile: any) => {
    setEditingProfile(profile)
    setIsEditing(true)
    setFormError('')
  }

  const cancelEditing = () => {
    setIsEditing(false)
    setEditingProfile(null)
    setFormData({ full_name: '', avatar_url: '' })
    setFormError('')
  }

  const openCreateDialog = () => {
    setIsCreateDialogOpen(true)
    setFormData({ full_name: '', avatar_url: '' })
    setFormError('')
  }

  const closeCreateDialog = () => {
    setIsCreateDialogOpen(false)
    setFormData({ full_name: '', avatar_url: '' })
    setFormError('')
  }

  if (loading) return (
    <div className="profile-manager">
      <div className="loading-state">
        <div className="loading-spinner" />
        <p>Loading profiles...</p>
      </div>
    </div>
  )

  if (error) return (
    <div className="profile-manager">
      <div className="error-state">
        <p>Error: {error}</p>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </div>
    </div>
  )

  return (
    <div className="profile-manager">
      <div className="header">
        <h2>Profile Manager</h2>
        <div className="user-info">
          <span>Welcome, {user?.email}</span>
          <Button variant="ghost" onClick={() => signOut()}>
            Sign Out
          </Button>
        </div>
      </div>

      {!currentUserProfile ? (
        <div className="create-profile">
          <h3>Create Your Profile</h3>
          <Button onClick={openCreateDialog} variant="primary">
              Create Profile
          </Button>
        </div>
      ) : (
        <div className="profile-section">
          <h3>Your Profile</h3>
          <div className="profile-card">
            {currentUserProfile.avatar_url && (
              <img 
                src={currentUserProfile.avatar_url} 
                alt="Avatar" 
                className="avatar"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none'
                }}
              />
            )}
            <div className="profile-info">
              <h4>{currentUserProfile.full_name || 'No name set'}</h4>
              <p>{currentUserProfile.email}</p>
              <p className="created-at">
                Created: {new Date(currentUserProfile.created_at).toLocaleDateString()}
              </p>
            </div>
            <div className="profile-actions">
              <Button 
                onClick={() => startEditing(currentUserProfile)}
                variant="secondary"
                size="sm"
              >
                Edit
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create Profile Dialog */}
      <Dialog
        isOpen={isCreateDialogOpen}
        onClose={closeCreateDialog}
        title="Create Profile"
        size="sm"
      >
        <DialogBody>
          <Form onSubmit={handleCreateProfile}>
            <FormGroup error={formError}>
              <FormLabel htmlFor="create_full_name" required>
                Full Name
              </FormLabel>
              <Input
                id="create_full_name"
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                placeholder="Enter your full name"
                required
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="create_avatar_url">
                Avatar URL
              </FormLabel>
              <Input
                id="create_avatar_url"
                type="url"
                value={formData.avatar_url}
                onChange={(e) => setFormData(prev => ({ ...prev, avatar_url: e.target.value }))}
                placeholder="Enter avatar URL (optional)"
              />
            </FormGroup>
            {formError && <FormError>{formError}</FormError>}
          </Form>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={closeCreateDialog}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="primary"
            loading={isCreating}
            onClick={handleCreateProfile}
          >
            Create Profile
          </Button>
        </DialogFooter>
      </Dialog>

      {/* Edit Profile Dialog */}
      <Dialog
        isOpen={isEditing}
        onClose={cancelEditing}
        title="Edit Profile"
        size="sm"
      >
        <DialogBody>
          <Form onSubmit={handleUpdateProfile}>
            <FormGroup error={formError}>
              <FormLabel htmlFor="edit_full_name" required>
                Full Name
              </FormLabel>
              <Input
                id="edit_full_name"
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                required
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="edit_avatar_url">
                Avatar URL
              </FormLabel>
              <Input
                id="edit_avatar_url"
                type="url"
                value={formData.avatar_url}
                onChange={(e) => setFormData(prev => ({ ...prev, avatar_url: e.target.value }))}
              />
            </FormGroup>
            {formError && <FormError>{formError}</FormError>}
          </Form>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={cancelEditing}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="primary"
            loading={isUpdating}
            onClick={handleUpdateProfile}
          >
                Save Changes
          </Button>
        </DialogFooter>
      </Dialog>

      {profiles.length > 0 && (
        <div className="all-profiles">
          <h3>All Profiles</h3>
          <div className="profiles-grid">
            {profiles.map((profile) => (
              <div key={profile.id} className="profile-card">
                {profile.avatar_url && (
                  <img 
                    src={profile.avatar_url} 
                    alt="Avatar" 
                    className="avatar"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none'
                    }}
                  />
                )}
                <div className="profile-info">
                  <h4>{profile.full_name || 'No name set'}</h4>
                  <p>{profile.email}</p>
                  <p className="created-at">
                    Created: {new Date(profile.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="profile-actions">
                  <Button 
                    onClick={() => startEditing(profile)}
                    variant="secondary"
                    size="sm"
                  >
                    Edit
                  </Button>
                  <Button 
                    onClick={() => handleDeleteProfile(profile.id)}
                    variant="error"
                    size="sm"
                    loading={isDeleting}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
